import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";

export default function Pricing() {
  const { lang } = useI18n();
  const { content } = useContent();
  const p = content?.pricing;
  if (!p) return null;
  const header = pick(p, lang, p.ru);
  const plans = p.plans || [];
  return (
    <section id="services" className="py-16 sm:py-20 lg:py-32 bg-muted/30 border-y border-border/40" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-4">
            {lang === "ru" ? "Тарифы" : "Pricing"}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight">{header.title}</h2>
          <p className="mt-4 text-base text-muted-foreground">{header.subtitle}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative p-5 sm:p-7 lg:p-9 rounded-3xl flex flex-col border ${
                plan.featured
                  ? "border-accent/50 bg-card glow-accent"
                  : "border-border bg-card"
              }`}
              data-testid={`pricing-plan-${i}`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                  {lang === "ru" ? "Рекомендуем" : "Recommended"}
                </div>
              )}
              <h3 className="font-heading text-2xl font-semibold mb-2">
                {lang === "ru" ? plan.ru_name : plan.en_name}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {lang === "ru" ? plan.ru_desc : plan.en_desc}
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {(lang === "ru" ? plan.ru_features : plan.en_features).map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm">
                    <Check className="size-4 mt-0.5 shrink-0 text-accent" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mb-6">
                {plan.free ? (
                  <div className="font-heading text-3xl font-bold">{lang === "ru" ? "Бесплатно" : "Free"}</div>
                ) : (
                  <div className="font-heading text-4xl font-bold">
                    {plan.price}<span className="text-xl text-muted-foreground ml-1">{plan.currency}</span>
                  </div>
                )}
              </div>
              <Link
                to="/#contacts"
                className={`w-full inline-flex items-center justify-center h-12 rounded-full font-semibold text-sm transition-all hover:-translate-y-0.5 ${
                  plan.featured
                    ? "bg-accent text-accent-foreground"
                    : "bg-foreground text-background"
                }`}
                data-testid={`pricing-cta-${i}`}
              >
                {lang === "ru" ? plan.ru_cta : plan.en_cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
